import {
  currencySchema,
  FUND_TRANSFER_SHAPES,
  FUND_TRANSFER_SIDES,
  FUND_TRANSFER_STATUSES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { generatedFileSchema } from './generated-file.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

const reasonSchema = z.string().trim().min(1).max(500);

const positiveMinorSchema = z
  .string()
  .regex(/^\d{1,18}$/, 'must be an integer in minor units')
  .refine((value) => /[1-9]/.test(value), 'must be above zero');

/** Which way a transfer moved, seen from one organization. */
export const FUND_TRANSFER_DIRECTIONS = ['out', 'in'] as const;

const organizationRefSchema = z.object({ id: z.uuid(), code: z.string(), name: z.string() });

/**
 * A line a transfer names, as either organization's reader may see it: the day,
 * the account and the amount — never the description or the counterparty,
 * which belong to that line's own organization's accountants.
 */
export const fundTransferLineSchema = z.object({
  id: z.uuid(),
  organizationAccountId: z.uuid(),
  accountLabel: z.string(),
  bookedOn: isoDaySchema,
  amount: moneySchema,
});

export type FundTransferLine = z.infer<typeof fundTransferLineSchema>;

export const fundTransferSchema = z.object({
  id: z.uuid(),
  fromOrganization: organizationRefSchema,
  toOrganization: organizationRefSchema,
  amount: moneySchema,
  reason: z.string(),
  status: z.enum(FUND_TRANSFER_STATUSES),
  shape: z.enum(FUND_TRANSFER_SHAPES),
  sourceLine: fundTransferLineSchema.nullable(),
  outLine: fundTransferLineSchema.nullable(),
  inLine: fundTransferLineSchema.nullable(),
  requestedByUserId: z.uuid(),
  requestedByName: z.string().nullable(),
  requestedAt: z.string(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  outMatchedByName: z.string().nullable(),
  outMatchedAt: z.string().nullable(),
  inMatchedByName: z.string().nullable(),
  inMatchedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  cancelledByName: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  /** Whether the reader's scope wholly holds each side, decided on the server as every write asks it. */
  fromInScope: z.boolean(),
  toInScope: z.boolean(),
});

export type FundTransfer = z.infer<typeof fundTransferSchema>;

/**
 * The transfer report — PRD §10 — is this list: one organization's transfers in
 * both directions, or one, by status and by the day each was asked for.
 */
export const fundTransferListInputSchema = paginationInputSchema
  .extend({
    organizationId: z.uuid().optional(),
    direction: z.enum(FUND_TRANSFER_DIRECTIONS).optional(),
    status: z.enum(FUND_TRANSFER_STATUSES).optional(),
    requestedFrom: isoDaySchema.optional(),
    requestedTo: isoDaySchema.optional(),
  })
  .refine(
    (input) => input.direction === undefined || input.organizationId !== undefined,
    'a direction is read from one organization',
  )
  .refine(
    (input) =>
      input.requestedFrom === undefined ||
      input.requestedTo === undefined ||
      input.requestedFrom <= input.requestedTo,
    'the period ends after it starts',
  );

export type FundTransferListInput = z.infer<typeof fundTransferListInputSchema>;

/**
 * The transfer list's filter, without its page: an export is every matching
 * row. Written out rather than derived, because the list input carries two
 * `refine`s and is no longer an object type `omit` can narrow.
 */
export const fundTransferExportInputSchema = z
  .object({
    organizationId: z.uuid().optional(),
    direction: z.enum(FUND_TRANSFER_DIRECTIONS).optional(),
    status: z.enum(FUND_TRANSFER_STATUSES).optional(),
    requestedFrom: isoDaySchema.optional(),
    requestedTo: isoDaySchema.optional(),
  })
  .refine(
    (input) => input.direction === undefined || input.organizationId !== undefined,
    'a direction is read from one organization',
  )
  .refine(
    (input) =>
      input.requestedFrom === undefined ||
      input.requestedTo === undefined ||
      input.requestedFrom <= input.requestedTo,
    'the period ends after it starts',
  );

export type FundTransferExportInput = z.infer<typeof fundTransferExportInputSchema>;

export const fundTransferOrganizationSchema = organizationRefSchema.extend({
  baseCurrency: currencySchema,
  inScope: z.boolean(),
});

export type FundTransferOrganization = z.infer<typeof fundTransferOrganizationSchema>;

export const createFundTransferInputSchema = z.object({
  fromOrganizationId: z.uuid(),
  toOrganizationId: z.uuid(),
  amountMinor: positiveMinorSchema,
  currency: currencySchema,
  reason: reasonSchema,
  sourceTransactionId: z.uuid().optional(),
});

export type CreateFundTransferInput = z.infer<typeof createFundTransferInputSchema>;

/** A line one side of a transfer can be completed with, read by that side's own accountants. */
export const fundTransferCandidateLineSchema = fundTransferLineSchema.extend({
  description: z.string().nullable(),
  counterpartyName: z.string().nullable(),
});

export type FundTransferCandidateLine = z.infer<typeof fundTransferCandidateLineSchema>;

/**
 * Money moved between two legal entities — docs/plans/05-finance-reconciliation.md
 * §5.8, roadmap Q15. Asked for and approved by two people in the source
 * organization's scope; each side completed by its own organization's
 * accountant; the list is readable from either side.
 */
export const fundTransferContract = {
  /** Every active organization, and whether the reader's scope wholly holds it. */
  organizations: oc
    .route({ method: 'GET', path: '/fund-transfers/organizations' })
    .errors(guarded)
    .input(paginationInputSchema)
    .output(paginated(fundTransferOrganizationSchema)),

  list: oc
    .route({ method: 'GET', path: '/fund-transfers' })
    .errors(guarded)
    .input(fundTransferListInputSchema)
    .output(paginated(fundTransferSchema)),

  /** The whole transfer filter as a file the worker writes — docs/decisions/0043-the-worker.md §7. */
  export: oc
    .route({ method: 'POST', path: '/fund-transfers/export' })
    .errors(guarded)
    .input(fundTransferExportInputSchema)
    .output(generatedFileSchema),

  /** A pass-through names the credit it forwards, which is allocated at once. */
  create: oc
    .route({ method: 'POST', path: '/fund-transfers' })
    .errors(guarded)
    .input(createFundTransferInputSchema)
    .output(z.object({ transfer: fundTransferSchema })),

  approve: oc
    .route({ method: 'POST', path: '/fund-transfers/{id}/approve' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ transfer: fundTransferSchema })),

  lines: oc
    .route({ method: 'GET', path: '/fund-transfers/{id}/lines' })
    .errors(guarded)
    .input(paginationInputSchema.extend({ id: z.uuid(), side: z.enum(FUND_TRANSFER_SIDES) }))
    .output(paginated(fundTransferCandidateLineSchema)),

  /** Names one side's line; the transfer completes when both sides are named. */
  complete: oc
    .route({ method: 'POST', path: '/fund-transfers/{id}/complete' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        side: z.enum(FUND_TRANSFER_SIDES),
        accountTransactionId: z.uuid(),
      }),
    )
    .output(z.object({ transfer: fundTransferSchema })),

  cancel: oc
    .route({ method: 'POST', path: '/fund-transfers/{id}/cancel' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(z.object({ transfer: fundTransferSchema })),
};
